/**
 * @fileoverview Dammen (Checkers) game logic hook
 * @description Manages game state, moves, captures, AI, and real-time sync
 */

import { useState, useCallback, useEffect, useRef } from "react";
import { supabase } from "@/lib/supabase/client";
import { useAuth } from "@/providers/AuthProvider";
import {
  DammenBoard,
  DammenCell,
  DammenPiece,
  PlayerId,
  BoardPosition,
  Move,
  GameStatus,
  DammenGameState,
  DammenResult,
  AIDifficulty,
  initializeBoard,
  positionToCoord,
  coordToPosition,
  isDarkSquare,
  isValidPosition,
} from "../types";

interface UseDammenOptions {
  sessionId: string;
  mode: "solo" | "versus";
  aiDifficulty?: AIDifficulty;
  onGameEnd?: (result: DammenResult) => void;
}

interface UseDammenReturn {
  board: DammenBoard;
  currentTurn: PlayerId;
  myPiece: PlayerId | null;
  isMyTurn: boolean;
  gameStatus: GameStatus;
  winner: PlayerId | null;
  selectedPosition: BoardPosition | null;
  validMoves: Move[];
  mustJumpFrom: BoardPosition | null;
  capturedPieces: { opa: number; oma: number };
  opponentInfo: { id: string; name: string; avatar_url?: string | null } | null;
  moveCount: number;
  isLoading: boolean;
  error: string | null;
  selectCell: (position: BoardPosition) => void;
  makeMove: (move: Move) => Promise<boolean>;
  forfeit: () => Promise<void>;
  reloadGameState: () => Promise<void>;
}

/**
 * Get all valid moves for a piece at a position
 * Kings in Dutch Dammen can move multiple squares diagonally (like a bishop)
 * Normal pieces can move forward only, but can CAPTURE in all directions
 */
const getValidMovesForPiece = (
  board: DammenBoard,
  pos: BoardPosition,
  piece: DammenPiece,
  mustCapture: boolean
): Move[] => {
  const moves: Move[] = [];
  const { row, col } = positionToCoord(pos);

  // All 4 diagonal directions
  const allDirections: { dr: number; dc: number }[] = [
    { dr: -1, dc: -1 },
    { dr: -1, dc: 1 },
    { dr: 1, dc: -1 },
    { dr: 1, dc: 1 },
  ];

  // Forward directions for normal piece movement (non-capture)
  const forwardDirections = piece.owner === "opa"
    ? allDirections.filter((d) => d.dr < 0) // Opa moves up
    : allDirections.filter((d) => d.dr > 0); // Oma moves down

  if (piece.type === "king") {
    // Kings can move and capture in all directions
    for (const { dr, dc } of allDirections) {
      // Kings can move multiple squares (flying kings / lange dam)
      let currentRow = row + dr;
      let currentCol = col + dc;
      let foundEnemy: BoardPosition | null = null;

      while (currentRow >= 0 && currentRow <= 7 && currentCol >= 0 && currentCol <= 7) {
        const currentPos = coordToPosition(currentRow, currentCol);
        const currentCell = board[currentPos];

        if (currentCell === null) {
          if (foundEnemy !== null) {
            // After jumping over enemy, all empty squares are valid landing spots
            moves.push({
              from: pos,
              to: currentPos,
              captures: [foundEnemy],
              isJump: true,
              promotesToKing: false,
            });
          } else if (!mustCapture) {
            // Regular move (no capture)
            moves.push({
              from: pos,
              to: currentPos,
              isJump: false,
              promotesToKing: false,
            });
          }
        } else if (currentCell.owner !== piece.owner) {
          // Enemy piece found
          if (foundEnemy !== null) {
            // Already found one enemy, can't jump two in one move
            break;
          }
          foundEnemy = currentPos;
        } else {
          // Own piece - blocked
          break;
        }

        currentRow += dr;
        currentCol += dc;
      }
    }
  } else {
    // Normal pieces: can MOVE forward only, but can CAPTURE in all directions
    
    // Check forward moves (non-capture)
    if (!mustCapture) {
      for (const { dr, dc } of forwardDirections) {
        const newRow = row + dr;
        const newCol = col + dc;

        if (newRow < 0 || newRow > 7 || newCol < 0 || newCol > 7) continue;

        const newPos = coordToPosition(newRow, newCol);
        const targetCell = board[newPos];

        if (targetCell === null) {
          // Empty square - can move here
          moves.push({
            from: pos,
            to: newPos,
            isJump: false,
            promotesToKing: checkPromotion(piece, newRow),
          });
        }
      }
    }

    // Check captures in ALL directions (including backward)
    for (const { dr, dc } of allDirections) {
      const newRow = row + dr;
      const newCol = col + dc;

      if (newRow < 0 || newRow > 7 || newCol < 0 || newCol > 7) continue;

      const newPos = coordToPosition(newRow, newCol);
      const targetCell = board[newPos];

      if (targetCell !== null && targetCell.owner !== piece.owner) {
        // Enemy piece - check if we can jump over
        const jumpRow = newRow + dr;
        const jumpCol = newCol + dc;

        if (jumpRow >= 0 && jumpRow <= 7 && jumpCol >= 0 && jumpCol <= 7) {
          const jumpPos = coordToPosition(jumpRow, jumpCol);
          if (board[jumpPos] === null) {
            // Can capture!
            moves.push({
              from: pos,
              to: jumpPos,
              captures: [newPos],
              isJump: true,
              promotesToKing: checkPromotion(piece, jumpRow),
            });
          }
        }
      }
    }
  }

  // If must capture, filter to only jump moves
  if (mustCapture) {
    return moves.filter((m) => m.isJump);
  }

  return moves;
};

/**
 * Check if piece should promote to king
 */
const checkPromotion = (piece: DammenPiece, newRow: number): boolean => {
  if (piece.type === "king") return false;
  // Opa promotes at row 0, Oma promotes at row 7
  return (piece.owner === "opa" && newRow === 0) || (piece.owner === "oma" && newRow === 7);
};

/**
 * Get all valid moves for a player
 */
const getAllValidMoves = (board: DammenBoard, player: PlayerId): Move[] => {
  const allMoves: Move[] = [];
  let hasCaptures = false;

  // First pass: find all moves and check if any captures exist
  for (let pos = 0; pos < 64; pos++) {
    const piece = board[pos];
    if (piece && piece.owner === player) {
      const moves = getValidMovesForPiece(board, pos, piece, false);
      if (moves.some((m) => m.isJump)) {
        hasCaptures = true;
      }
      allMoves.push(...moves);
    }
  }

  // If captures exist, filter to only captures (mandatory capture rule)
  if (hasCaptures) {
    return allMoves.filter((m) => m.isJump);
  }

  return allMoves;
};

/**
 * Check for multi-jump possibilities after a capture
 */
const getMultiJumpMoves = (
  board: DammenBoard,
  pos: BoardPosition,
  piece: DammenPiece
): Move[] => {
  return getValidMovesForPiece(board, pos, piece, true);
};

/**
 * Apply a move to the board (returns new board)
 */
const applyMove = (board: DammenBoard, move: Move): DammenBoard => {
  const newBoard = [...board];
  const piece = newBoard[move.from];

  if (!piece) return newBoard;

  // Move piece
  newBoard[move.from] = null;
  newBoard[move.to] = {
    ...piece,
    type: move.promotesToKing ? "king" : piece.type,
  };

  // Remove captured pieces
  if (move.captures) {
    for (const capturePos of move.captures) {
      newBoard[capturePos] = null;
    }
  }

  return newBoard;
};

/**
 * Count pieces for each player
 */
const countPieces = (board: DammenBoard): { opa: number; oma: number } => {
  let opa = 0;
  let oma = 0;

  for (const cell of board) {
    if (cell?.owner === "opa") opa++;
    if (cell?.owner === "oma") oma++;
  }

  return { opa, oma };
};

/**
 * Check if game is over
 */
const checkGameOver = (
  board: DammenBoard,
  currentPlayer: PlayerId
): { isOver: boolean; winner: PlayerId | null; isDraw: boolean } => {
  const pieces = countPieces(board);

  // Check if a player has no pieces left
  if (pieces.opa === 0) {
    return { isOver: true, winner: "oma", isDraw: false };
  }
  if (pieces.oma === 0) {
    return { isOver: true, winner: "opa", isDraw: false };
  }

  // Check if current player has no valid moves
  const validMoves = getAllValidMoves(board, currentPlayer);
  if (validMoves.length === 0) {
    // Current player can't move - they lose
    const winner = currentPlayer === "opa" ? "oma" : "opa";
    return { isOver: true, winner, isDraw: false };
  }

  return { isOver: false, winner: null, isDraw: false };
};

// ===== AI Logic =====

/**
 * Evaluate board position for AI
 */
const evaluateBoard = (board: DammenBoard, aiPlayer: PlayerId): number => {
  let score = 0;
  const opponent = aiPlayer === "opa" ? "oma" : "opa";

  for (let pos = 0; pos < 64; pos++) {
    const piece = board[pos];
    if (!piece) continue;

    const { row } = positionToCoord(pos);
    let pieceValue = piece.type === "king" ? 5 : 3;

    // Position bonuses
    if (piece.type === "normal") {
      // Advance bonus
      if (piece.owner === "opa") {
        pieceValue += (7 - row) * 0.1; // Closer to promotion
      } else {
        pieceValue += row * 0.1;
      }
    }

    // Center control bonus
    const { col } = positionToCoord(pos);
    if (col >= 2 && col <= 5 && row >= 2 && row <= 5) {
      pieceValue += 0.5;
    }

    if (piece.owner === aiPlayer) {
      score += pieceValue;
    } else {
      score -= pieceValue;
    }
  }

  return score;
};

/**
 * Minimax algorithm with alpha-beta pruning
 */
const minimax = (
  board: DammenBoard,
  depth: number,
  alpha: number,
  beta: number,
  isMaximizing: boolean,
  aiPlayer: PlayerId
): number => {
  const currentPlayer = isMaximizing ? aiPlayer : (aiPlayer === "opa" ? "oma" : "opa");
  const gameState = checkGameOver(board, currentPlayer);

  if (gameState.isOver) {
    if (gameState.winner === aiPlayer) return 1000 + depth;
    if (gameState.winner !== null) return -1000 - depth;
    return 0;
  }

  if (depth === 0) {
    return evaluateBoard(board, aiPlayer);
  }

  const moves = getAllValidMoves(board, currentPlayer);

  if (isMaximizing) {
    let maxEval = -Infinity;
    for (const move of moves) {
      const newBoard = applyMove(board, move);
      const evalScore = minimax(newBoard, depth - 1, alpha, beta, false, aiPlayer);
      maxEval = Math.max(maxEval, evalScore);
      alpha = Math.max(alpha, evalScore);
      if (beta <= alpha) break;
    }
    return maxEval;
  } else {
    let minEval = Infinity;
    for (const move of moves) {
      const newBoard = applyMove(board, move);
      const evalScore = minimax(newBoard, depth - 1, alpha, beta, true, aiPlayer);
      minEval = Math.min(minEval, evalScore);
      beta = Math.min(beta, evalScore);
      if (beta <= alpha) break;
    }
    return minEval;
  }
};

/**
 * Calculate AI move based on difficulty
 */
const calculateAIMove = (
  board: DammenBoard,
  aiPlayer: PlayerId,
  difficulty: AIDifficulty
): Move | null => {
  const moves = getAllValidMoves(board, aiPlayer);
  if (moves.length === 0) return null;

  // Easy: Random move (60% random, 40% captures if available)
  if (difficulty === "easy") {
    const captures = moves.filter((m) => m.isJump);
    if (captures.length > 0 && Math.random() < 0.4) {
      return captures[Math.floor(Math.random() * captures.length)];
    }
    return moves[Math.floor(Math.random() * moves.length)];
  }

  // Medium: Prioritize captures, some randomness
  if (difficulty === "medium") {
    const captures = moves.filter((m) => m.isJump);
    if (captures.length > 0) {
      // Multi-captures preferred
      const multiCaptures = captures.filter((m) => m.captures && m.captures.length > 1);
      if (multiCaptures.length > 0) {
        return multiCaptures[Math.floor(Math.random() * multiCaptures.length)];
      }
      return captures[Math.floor(Math.random() * captures.length)];
    }

    // 30% chance of random move
    if (Math.random() < 0.3) {
      return moves[Math.floor(Math.random() * moves.length)];
    }

    // Use shallow minimax
    let bestMove = moves[0];
    let bestScore = -Infinity;
    for (const move of moves) {
      const newBoard = applyMove(board, move);
      const score = minimax(newBoard, 2, -Infinity, Infinity, false, aiPlayer);
      if (score > bestScore) {
        bestScore = score;
        bestMove = move;
      }
    }
    return bestMove;
  }

  // Hard: Full minimax with depth 4
  let bestMove = moves[0];
  let bestScore = -Infinity;
  for (const move of moves) {
    const newBoard = applyMove(board, move);
    const score = minimax(newBoard, 4, -Infinity, Infinity, false, aiPlayer);
    if (score > bestScore) {
      bestScore = score;
      bestMove = move;
    }
  }
  return bestMove;
};

// ===== Main Hook =====

export const useDammen = ({
  sessionId,
  mode,
  aiDifficulty = "medium",
  onGameEnd,
}: UseDammenOptions): UseDammenReturn => {
  const { activeProfile } = useAuth();

  // Game state
  const [board, setBoard] = useState<DammenBoard>(initializeBoard());
  const [currentTurn, setCurrentTurn] = useState<PlayerId>("opa");
  const [myPiece, setMyPiece] = useState<PlayerId | null>(null);
  const [gameStatus, setGameStatus] = useState<GameStatus>("waiting");
  const [winner, setWinner] = useState<PlayerId | null>(null);
  const [selectedPosition, setSelectedPosition] = useState<BoardPosition | null>(null);
  const [validMoves, setValidMoves] = useState<Move[]>([]);
  const [mustJumpFrom, setMustJumpFrom] = useState<BoardPosition | null>(null);
  const [capturedPieces, setCapturedPieces] = useState({ opa: 0, oma: 0 });
  const [opponentInfo, setOpponentInfo] = useState<{
    id: string;
    name: string;
    avatar_url?: string | null;
  } | null>(null);
  const [moveCount, setMoveCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [gameStartTime] = useState<number>(Date.now());

  // Refs
  const isProcessingMove = useRef(false);
  const subscriptionRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const activeProfileIdRef = useRef<string | null>(activeProfile?.id || null);
  const onGameEndRef = useRef(onGameEnd);
  const gameEndTriggeredRef = useRef(false);

  // Keep refs in sync
  useEffect(() => {
    activeProfileIdRef.current = activeProfile?.id || null;
  }, [activeProfile?.id]);

  useEffect(() => {
    onGameEndRef.current = onGameEnd;
  }, [onGameEnd]);

  const isMyTurn = myPiece === currentTurn && gameStatus === "playing";

  /**
   * Load game state from session
   */
  const loadGameState = useCallback(async () => {
    if (!sessionId || !activeProfile?.id) return;

    try {
      setIsLoading(true);
      setError(null);

      const { data: session, error: sessionError } = await supabase
        .from("game_sessions")
        .select("game_state, status, participants")
        .eq("id", sessionId)
        .single();

      if (sessionError) throw sessionError;

      if (!session) {
        setError("Sessie niet gevonden");
        return;
      }

      const gameState = session.game_state as DammenGameState;

      if (gameState) {
        setBoard(gameState.board || initializeBoard());
        setCurrentTurn(gameState.currentTurn || "opa");
        setCapturedPieces(gameState.capturedPieces || { opa: 0, oma: 0 });
        setMoveCount(gameState.moveHistory?.length || 0);

        // Determine my piece
        if (gameState.playerAssignments) {
          const myAssignment = gameState.playerAssignments[activeProfile.id];
          setMyPiece(myAssignment || null);
        }

        // Set must jump if mid-capture
        if (gameState.mustContinueJump) {
          setMustJumpFrom(gameState.mustContinueJump.from);
        }

        // Game status
        if (gameState.status === "won" || gameState.status === "lost" || session.status === "completed") {
          const myAssignment = gameState.playerAssignments?.[activeProfile.id];
          const isWinner = gameState.winnerId === activeProfile.id;
          const winnerPiece = isWinner 
            ? myAssignment 
            : (myAssignment === "opa" ? "oma" : "opa");
          
          setGameStatus(isWinner ? "won" : "lost");
          setWinner(winnerPiece || null);

          // Trigger onGameEnd if game is already completed (only once)
          if (!gameEndTriggeredRef.current && onGameEndRef.current) {
            gameEndTriggeredRef.current = true;
            const loadedResult: DammenResult = {
              winnerId: gameState.winnerId || null,
              winnerPiece: winnerPiece || null,
              isDraw: false,
              isForfeit: false,
              moveCount: gameState.moveHistory?.length || 0,
              capturedByOpa: gameState.capturedPieces?.opa || 0,
              capturedByOma: gameState.capturedPieces?.oma || 0,
              duration: 0,
            };
            onGameEndRef.current(loadedResult);
          }
        } else if (session.status === "active") {
          setGameStatus("playing");
        } else if (session.status === "waiting") {
          setGameStatus("waiting");
        }
      }

      // Get opponent info
      const participants = session.participants as Array<{
        user_id: string;
        display_name: string;
        avatar_url?: string;
      }>;
      const opponent = participants?.find((p) => p.user_id !== activeProfile.id);
      if (opponent) {
        setOpponentInfo({
          id: opponent.user_id,
          name: opponent.display_name,
          avatar_url: opponent.avatar_url,
        });
      }
    } catch (err) {
      console.error("Error loading game state:", err);
      setError("Kon spel niet laden");
    } finally {
      setIsLoading(false);
    }
  }, [sessionId, activeProfile?.id]);

  /**
   * Subscribe to real-time updates
   */
  const subscribeToUpdates = useCallback(() => {
    if (!sessionId || mode === "solo") return;

    const channel = supabase
      .channel(`dammen:${sessionId}`)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "game_sessions",
          filter: `id=eq.${sessionId}`,
        },
        (payload) => {
          const newData = payload.new as any;
          const newGameState = newData.game_state as DammenGameState;
          
          if (newGameState) {
            setBoard(newGameState.board);
            setCurrentTurn(newGameState.currentTurn);
            setCapturedPieces(newGameState.capturedPieces);
            setMoveCount(newGameState.moveHistory?.length || 0);
            setMustJumpFrom(newGameState.mustContinueJump?.from || null);

            // Update myPiece if available
            const currentUserId = activeProfileIdRef.current;
            if (newGameState.playerAssignments && currentUserId) {
              const assignment = newGameState.playerAssignments[currentUserId];
              if (assignment) {
                setMyPiece(assignment);
              }
            }

            // Update game status based on session status
            if (newData.status === "active") {
              setGameStatus("playing");
            }

            // Check game over from game state (for real-time sync)
            if (newGameState.status === "won" || newGameState.status === "lost") {
              const myAssignment = newGameState.playerAssignments?.[currentUserId || ""];
              const isWinner = newGameState.winnerId === currentUserId;
              const winnerPiece = isWinner ? myAssignment : (myAssignment === "opa" ? "oma" : "opa");
              
              setWinner(winnerPiece || null);
              setGameStatus(isWinner ? "won" : "lost");

              // Trigger onGameEnd for the losing player (only once)
              if (!isWinner && !gameEndTriggeredRef.current && onGameEndRef.current) {
                gameEndTriggeredRef.current = true;
                const resultForLoser: DammenResult = {
                  winnerId: newGameState.winnerId || null,
                  winnerPiece: winnerPiece || null,
                  isDraw: false,
                  isForfeit: false,
                  moveCount: newGameState.moveHistory?.length || 0,
                  capturedByOpa: newGameState.capturedPieces?.opa || 0,
                  capturedByOma: newGameState.capturedPieces?.oma || 0,
                  duration: 0,
                };
                onGameEndRef.current(resultForLoser);
              }
            } else {
              // Check game over by analyzing board
              const gameOver = checkGameOver(newGameState.board, newGameState.currentTurn);
              if (gameOver.isOver) {
                setWinner(gameOver.winner);
                const myAssignment = newGameState.playerAssignments?.[currentUserId || ""];
                setGameStatus(gameOver.winner === myAssignment ? "won" : "lost");
              }
            }
          }

          if (newData.status === "completed") {
            // Game ended - handled above via game_state
          }
        }
      )
      .subscribe();

    subscriptionRef.current = channel;
  }, [sessionId, mode]);

  // Initialize
  useEffect(() => {
    loadGameState();
    subscribeToUpdates();

    return () => {
      if (subscriptionRef.current) {
        supabase.removeChannel(subscriptionRef.current);
      }
    };
  }, [loadGameState, subscribeToUpdates]);

  /**
   * Select a cell
   */
  const selectCell = useCallback(
    (position: BoardPosition) => {
      if (!isMyTurn || isProcessingMove.current) return;

      const piece = board[position];

      // If we have a selected piece and this is a valid move destination
      if (selectedPosition !== null) {
        const move = validMoves.find((m) => m.to === position);
        if (move) {
          // Execute the move
          makeMove(move);
          return;
        }
      }

      // Check if must jump from specific position
      if (mustJumpFrom !== null && position !== mustJumpFrom) {
        return; // Can only select the piece that must continue jumping
      }

      // Select a piece
      if (piece && piece.owner === myPiece) {
        setSelectedPosition(position);
        const moves = mustJumpFrom
          ? getMultiJumpMoves(board, position, piece)
          : getValidMovesForPiece(
              board,
              position,
              piece,
              getAllValidMoves(board, myPiece!).some((m) => m.isJump)
            );
        setValidMoves(moves);
      } else if (selectedPosition !== null) {
        // Deselect
        setSelectedPosition(null);
        setValidMoves([]);
      }
    },
    [board, isMyTurn, myPiece, selectedPosition, validMoves, mustJumpFrom]
  );

  /**
   * Make a move
   */
  const makeMove = useCallback(
    async (move: Move): Promise<boolean> => {
      if (!isMyTurn || isProcessingMove.current || !activeProfile?.id) return false;

      isProcessingMove.current = true;

      try {
        // Apply move locally
        const newBoard = applyMove(board, move);
        const piece = newBoard[move.to];

        // Check for multi-jump
        let continueJump: { from: BoardPosition; playerId: PlayerId } | null = null;
        if (move.isJump && piece) {
          const multiJumps = getMultiJumpMoves(newBoard, move.to, piece);
          if (multiJumps.length > 0) {
            continueJump = { from: move.to, playerId: myPiece! };
          }
        }

        // Update captured pieces count
        const newCaptured = { ...capturedPieces };
        if (move.captures) {
          for (const capPos of move.captures) {
            const capturedPiece = board[capPos];
            if (capturedPiece) {
              if (capturedPiece.owner === "opa") newCaptured.oma++;
              else newCaptured.opa++;
            }
          }
        }

        // Determine next turn
        const nextTurn = continueJump ? currentTurn : (currentTurn === "opa" ? "oma" : "opa");

        // Check game over
        const gameOver = checkGameOver(newBoard, nextTurn);

        // Get current game state
        const { data: session } = await supabase
          .from("game_sessions")
          .select("game_state")
          .eq("id", sessionId)
          .single();

        const currentGameState = session?.game_state as DammenGameState;
        const moveHistory = [...(currentGameState?.moveHistory || []), move];

        // Update game state
        const newGameState: DammenGameState = {
          ...currentGameState,
          board: newBoard,
          currentTurn: nextTurn,
          moveHistory,
          capturedPieces: newCaptured,
          mustContinueJump: continueJump,
          status: gameOver.isOver ? (gameOver.winner === myPiece ? "won" : "lost") : "playing",
          winnerId: gameOver.isOver && gameOver.winner
            ? Object.entries(currentGameState?.playerAssignments || {}).find(
                ([, piece]) => piece === gameOver.winner
              )?.[0] || null
            : null,
        };

        // Update session
        const updateData: Record<string, unknown> = {
          game_state: newGameState,
          updated_at: new Date().toISOString(),
        };

        if (gameOver.isOver) {
          updateData.status = "completed";
          updateData.completed_at = new Date().toISOString();
          updateData.winner_user_id = newGameState.winnerId;
        }

        await supabase.from("game_sessions").update(updateData).eq("id", sessionId);

        // Update local state
        setBoard(newBoard);
        setCurrentTurn(nextTurn);
        setCapturedPieces(newCaptured);
        setMoveCount(moveHistory.length);
        setSelectedPosition(null);
        setValidMoves([]);

        if (continueJump) {
          setMustJumpFrom(continueJump.from);
        } else {
          setMustJumpFrom(null);
        }

        if (gameOver.isOver) {
          setWinner(gameOver.winner);
          setGameStatus(gameOver.winner === myPiece ? "won" : "lost");

          // Trigger callback
          if (onGameEnd) {
            const result: DammenResult = {
              winnerId: newGameState.winnerId || null,
              winnerPiece: gameOver.winner,
              isDraw: gameOver.isDraw,
              isForfeit: false,
              moveCount: moveHistory.length,
              capturedByOpa: newCaptured.opa,
              capturedByOma: newCaptured.oma,
              duration: Math.floor((Date.now() - gameStartTime) / 1000),
            };
            onGameEnd(result);
          }
        }

        // AI move (solo mode)
        if (mode === "solo" && !gameOver.isOver && !continueJump && nextTurn !== myPiece) {
          setTimeout(() => {
            makeAIMove(newBoard, nextTurn);
          }, 500);
        }

        return true;
      } catch (err) {
        console.error("Error making move:", err);
        setError("Kon zet niet uitvoeren");
        return false;
      } finally {
        isProcessingMove.current = false;
      }
    },
    [
      board,
      isMyTurn,
      activeProfile?.id,
      sessionId,
      currentTurn,
      capturedPieces,
      myPiece,
      mode,
      onGameEnd,
      gameStartTime,
    ]
  );

  /**
   * AI makes a move
   */
  const makeAIMove = useCallback(
    async (currentBoard: DammenBoard, aiPlayer: PlayerId) => {
      if (isProcessingMove.current) return;

      isProcessingMove.current = true;

      try {
        const aiMove = calculateAIMove(currentBoard, aiPlayer, aiDifficulty);
        if (!aiMove) {
          // AI has no moves - game over
          setWinner(myPiece);
          setGameStatus("won");
          return;
        }

        // Apply AI move
        let newBoard = applyMove(currentBoard, aiMove);
        const moveHistory: Move[] = [aiMove];
        const newCaptured = { ...capturedPieces };

        if (aiMove.captures) {
          for (const capPos of aiMove.captures) {
            const capturedPiece = currentBoard[capPos];
            if (capturedPiece) {
              if (capturedPiece.owner === "opa") newCaptured.oma++;
              else newCaptured.opa++;
            }
          }
        }

        // Check for multi-jumps
        let piece = newBoard[aiMove.to];
        if (aiMove.isJump && piece) {
          let multiJumps = getMultiJumpMoves(newBoard, aiMove.to, piece);
          while (multiJumps.length > 0) {
            await new Promise((r) => setTimeout(r, 300)); // Visual delay

            const nextJump = multiJumps[Math.floor(Math.random() * multiJumps.length)];
            newBoard = applyMove(newBoard, nextJump);
            moveHistory.push(nextJump);

            if (nextJump.captures) {
              for (const capPos of nextJump.captures) {
                if (newCaptured.opa !== undefined) newCaptured.oma++;
              }
            }

            piece = newBoard[nextJump.to];
            if (piece) {
              multiJumps = getMultiJumpMoves(newBoard, nextJump.to, piece);
            } else {
              break;
            }
          }
        }

        // Update state
        setBoard(newBoard);
        setCapturedPieces(newCaptured);
        setMoveCount((prev) => prev + moveHistory.length);

        // Check game over
        const nextTurn = myPiece!;
        const gameOver = checkGameOver(newBoard, nextTurn);

        if (gameOver.isOver) {
          setWinner(gameOver.winner);
          setGameStatus(gameOver.winner === myPiece ? "won" : "lost");

          if (onGameEnd) {
            const result: DammenResult = {
              winnerId: gameOver.winner === myPiece ? activeProfile?.id || null : null,
              winnerPiece: gameOver.winner,
              isDraw: gameOver.isDraw,
              isForfeit: false,
              moveCount: moveCount + moveHistory.length,
              capturedByOpa: newCaptured.opa,
              capturedByOma: newCaptured.oma,
              duration: Math.floor((Date.now() - gameStartTime) / 1000),
            };
            onGameEnd(result);
          }
        } else {
          setCurrentTurn(nextTurn);
        }
      } finally {
        isProcessingMove.current = false;
      }
    },
    [aiDifficulty, capturedPieces, myPiece, onGameEnd, moveCount, activeProfile?.id, gameStartTime]
  );

  /**
   * Forfeit the game
   */
  const forfeit = useCallback(async () => {
    if (!activeProfile?.id) return;

    try {
      const { data: session } = await supabase
        .from("game_sessions")
        .select("game_state")
        .eq("id", sessionId)
        .single();

      const gameState = session?.game_state as DammenGameState;
      const winnerPiece = myPiece === "opa" ? "oma" : "opa";
      const winnerId = Object.entries(gameState?.playerAssignments || {}).find(
        ([, piece]) => piece === winnerPiece
      )?.[0];

      await supabase
        .from("game_sessions")
        .update({
          status: "completed",
          completed_at: new Date().toISOString(),
          winner_user_id: winnerId,
          game_state: {
            ...gameState,
            status: "forfeit",
            winnerId,
          },
        })
        .eq("id", sessionId);

      setGameStatus("forfeit");
      setWinner(winnerPiece);

      if (onGameEnd) {
        const result: DammenResult = {
          winnerId: winnerId || null,
          winnerPiece,
          isDraw: false,
          isForfeit: true,
          moveCount,
          capturedByOpa: capturedPieces.opa,
          capturedByOma: capturedPieces.oma,
          duration: Math.floor((Date.now() - gameStartTime) / 1000),
        };
        onGameEnd(result);
      }
    } catch (err) {
      console.error("Error forfeiting:", err);
      setError("Kon niet opgeven");
    }
  }, [
    activeProfile?.id,
    sessionId,
    myPiece,
    onGameEnd,
    moveCount,
    capturedPieces,
    gameStartTime,
  ]);

  return {
    board,
    currentTurn,
    myPiece,
    isMyTurn,
    gameStatus,
    winner,
    selectedPosition,
    validMoves,
    mustJumpFrom,
    capturedPieces,
    opponentInfo,
    moveCount,
    isLoading,
    error,
    selectCell,
    makeMove,
    forfeit,
    reloadGameState: loadGameState,
  };
};

export { getAllValidMoves, applyMove, checkGameOver, calculateAIMove };

